import type { Copy } from './types'

export const en: Copy = {
  nativeName: 'English',
  a11y: {
    mainNav: 'Main navigation',
    logo: 'La Reina Beauty Salon',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    instagram: 'La Reina Beauty Salon on Instagram',
    tiktok: 'La Reina Beauty Salon on TikTok',
    loading: 'Loading La Reina Beauty Salon',
    fiveStars: '5 out of 5 stars',
    serviceTabs: 'Service categories',
    languageSwitcher: 'Site language',
    switchTo: 'View this site in English',
    whatsappCta: 'Book on WhatsApp at',
  },
  whatsappMessage: "Hi La Reina Beauty Salon, I'd like to book an appointment",
  walkIns: 'Walk-ins Welcome · No Appointment Needed',
  ctaShort: 'No Appointment Needed',
  nav: {
    links: [
      { id: 'experiencia', label: 'Experience' },
      { id: 'servicios', label: 'Services' },
      { id: 'resenas', label: 'Reviews' },
      { id: 'nosotras', label: 'About Us' },
      { id: 'horario', label: 'Hours' },
      { id: 'contacto', label: 'Contact' },
    ],
  },
  hero: {
    location: 'Staten Island, New York',
    title: 'Your hair. Your crown. Your moment.',
    subtitle: 'Artistry, precision and passion — in every single service.',
    secondaryCta: 'View services',
    ratingNote: 'hundreds of real reviews',
  },
  highlights: {
    eyebrow: 'The Art of La Reina',
    title: 'What if one place had everything your hair actually needs?',
    intro:
      'A team that truly knows what it is doing, and results that speak for themselves — no more salon hopping.',
    items: [
      {
        title: 'A cut that genuinely suits you',
        lead: 'Not the cut the stylist felt like giving you.',
        body: 'The cut you asked for. The one that has been on your mind for weeks. At La Reina we listen before we pick up the scissors, because a great cut does not start with the hands — it starts with the ears.',
      },
      {
        title: 'The Dominican blowout everyone is looking for',
        lead: 'There are blow drys. And then there is the La Reina Dominican blowout.',
        body: 'Soft. Shiny. Full of movement. Full of life. The kind of result that makes people ask what you did — and you just smile.',
      },
      {
        title: 'Color that transforms without destroying',
        lead: 'So many women fear color after one bad experience.',
        body: 'Here color is science and art at once. We choose the technique based on your hair type, your history and your goal — so you get the result you want without compromising its health.',
      },
      {
        title: 'Premium treatments that truly restore',
        lead: 'Dry? Dull? Brittle? Damaged by years of chemicals or heat?',
        body: 'You do not have to settle — you need the right treatment. We offer the most advanced protocols on the market: the ones that nourish and bring hair back to life from the inside out.',
      },
      {
        title: '100% human hair extensions',
        lead: 'Longer? Fuller? A complete transformation?',
        body: 'We do not just install extensions — we sell 100% human hair right here in the salon. No need to source hair somewhere else and come back. You find everything in one place.',
      },
      {
        title: 'Your special event deserves a special look',
        lead: 'Graduation. Wedding. Quinceañera. Anniversary. That special dinner.',
        body: 'That moment matters, and so does your hair. At La Reina we get you ready for the moments you never forget.',
      },
    ],
    eventTestimonial: {
      quote:
        'Nelly did an amazing job on my hair for my daughter’s graduation. Wash, cut and blowout. So happy with my cut. Carmen was also amazing with my daughter’s hair for her graduation.',
      author: 'Carmen C.',
    },
  },
  services: {
    eyebrow: 'All of our services',
    title: 'Everything in one place',
    intro: 'Pick a category to see the full detail of every service.',
    countLabel: 'services',
    categories: [
      {
        label: 'Cuts & Styling',
        title: 'Cuts and general styling',
        columnLabel: 'Description',
        imageAlt: 'Layered cut with a Dominican blow dry and glossy finish',
        rows: [
          {
            name: 'Hair Cut',
            description: 'A custom cut shaped to your face type, texture and style goal',
          },
          {
            name: 'Trimming / Ends',
            description: 'Removing damaged ends to encourage healthy growth',
          },
          {
            name: 'Blow Dry',
            description: 'Professional blow dry with Dominican technique for maximum shine and softness',
          },
          {
            name: 'Wrap',
            description: 'Wrapping technique for a sleek, soft finish with natural movement',
          },
          {
            name: 'Rollers',
            description: 'Roller set for defined curls and long-lasting waves',
          },
          {
            name: 'Rollers + Blow Dry',
            description: 'Rollers combined with a blow dry for extra volume and durability',
          },
          {
            name: 'Wash + Set',
            description: 'Full wash plus a finished style ready for any occasion',
          },
          {
            name: 'Up Styles',
            description: 'Elegant updos for events, weddings and special occasions',
          },
          {
            name: 'Curls',
            description: 'Defined, long-lasting curls with professional technique',
          },
          {
            name: 'Pony Tail',
            description: 'A styled ponytail, from the most natural to the most glamorous',
          },
        ],
      },
      {
        label: 'Color Services',
        title: 'Color services',
        columnLabel: 'Description',
        imageAlt: 'Deep wine color with defined waves created in the salon',
        rows: [
          {
            name: 'Permanent Color',
            description:
              'High-coverage permanent color with professional formulas for rich, long-lasting results',
          },
          {
            name: 'Highlights',
            description:
              'Strategically placed highlights that add dimension, light and movement to your hair',
          },
          {
            name: 'Balayage',
            description: 'Hand-painted graduated technique for a natural, sophisticated effect',
          },
          {
            name: 'Color Rinse',
            description:
              'Temporary color rinse that adds shine and tone without compromising the hair structure',
          },
          {
            name: 'Semi-Permanent',
            description: 'Semi-permanent color that enriches your tone without harsh chemical damage',
          },
          {
            name: 'Color Reverse',
            description: 'Specialized process to safely reverse or correct previous color',
          },
          {
            name: 'Low Lights',
            description: 'Strategic darker pieces that add depth and dimension to your base color',
          },
        ],
      },
      {
        label: 'Premium Treatments',
        title: 'Premium restoration treatments',
        columnLabel: 'What it is for',
        imageAlt: 'Black hair treated to a mirror shine with voluminous waves',
        rows: [
          {
            name: 'Bio Plastia',
            description:
              'Seals the hair fiber, eliminates frizz and delivers intense shine and extreme softness from the very first session',
          },
          {
            name: 'Botanical Smoothing',
            description:
              'Progressive smoothing with 100% botanical ingredients — no harsh chemicals, no damage to the natural hair structure',
          },
          {
            name: 'Restructuring',
            description:
              'Deep fiber-by-fiber rebuilding for hair severely damaged by heat, chemicals or bleaching',
          },
          {
            name: 'Intensive Nutrition',
            description:
              'Highly concentrated active nutrition that restores strength, elasticity and vitality to weakened hair',
          },
          {
            name: 'Intensive Hydration',
            description:
              'Deep, long-lasting hydration for dry, dull and lifeless hair — visible results from the first use',
          },
          {
            name: 'Deep Conditioning',
            description:
              'Intensive conditioning that restores softness, shine and manageability in a single session',
          },
          {
            name: 'Relaxer',
            description:
              'Professional chemical straightening applied with precision and care to respect hair integrity',
          },
          {
            name: 'Texture',
            description:
              'Texturizing process to define, soften or transform your natural texture with full control',
          },
        ],
      },
      {
        label: 'Extensions & Braids',
        title: 'Extensions and braids',
        columnLabel: 'Description',
        imageAlt: 'Braids with twists and gold accessories on a child’s hair',
        rows: [
          {
            name: 'Hair Extensions',
            description:
              'Professional extensions with 100% human hair for more length, volume and a full transformation',
          },
          {
            name: 'Clips On',
            description:
              'Easy-to-place removable extensions — ideal for temporary changes with no commitment',
          },
          {
            name: 'Glued',
            description: 'Extensions bonded with professional adhesive for a natural, lasting result',
          },
          {
            name: 'Sewed In',
            description:
              'Extensions sewn onto braids — the safest, longest-lasting technique to protect your natural hair',
          },
          {
            name: 'Braids',
            description: 'Artistic braids from the simplest styles to the most elaborate and creative',
          },
          {
            name: 'Pony Tail with Extension',
            description: 'A ponytail enhanced with added hair for instant length and volume',
          },
        ],
      },
    ],
    callout: {
      lead: 'We ',
      accent: 'sell 100% human hair',
      rest: ' right here in the salon. No need to go anywhere else — you find the hair and the install in one place.',
    },
  },
  testimonials: {
    eyebrow: 'Verified reviews',
    title: 'Real clients. Real words. Real results.',
    featured: {
      quote:
        'Absolutely thrilled with the service. There is nothing like a Dominican blowout here. They truly care about your hair and make sure you leave happy. This is my favorite place!',
      author: 'Yudelki V.',
    },
    items: [
      {
        quote:
          'Amazing experience as a girl with natural 4C hair! They colored, conditioned and straightened my hair. The owner was so kind in teaching me how to maintain my color. The staff is incredibly sweet! 10/10 recommend.',
        author: 'CCMedia Mogul',
        meta: 'Local Guide',
      },
      {
        quote:
          'First time in the United States and I needed an appointment before a dinner. They were incredible! I have never seen my hair so healthy and straight. I would absolutely come back and recommend it.',
        author: 'Aaliyah L.',
        meta: 'Local Guide',
      },
      {
        quote:
          'I was delighted with the service I received at this salon. The professionalism, the expertise and the work are more than excellent. I am definitely a client for life!',
        author: 'Melissa B.',
      },
      {
        quote:
          'They did a wash and set for me and also installed extensions. I loved it. Such kind people. I would definitely come back.',
        author: 'Alexis C.',
        meta: 'Local Guide',
      },
    ],
  },
  transition: {
    words: [
      'La Reina',
      'Beauty Salon',
      'Walk in a client. Walk out a Queen.',
      'Dominican Styling',
    ],
    stats: {
      precision: { value: 'Precision', label: 'In every service' },
      services: { value: '', label: 'Services' },
      humanHair: { value: '100%', label: 'Human hair' },
      hairTypes: { value: 'Every', label: 'Hair type' },
    },
  },
  founders: {
    eyebrow: 'Our story',
    title: 'Meet the queens behind La Reina',
    intro:
      'Nelly Vargas and Mervil Granja did not open La Reina to be just another salon on the block. They opened it with a mission.',
    quote:
      'La Reina in English meaning Queen — is how we would like for our customers to feel when they leave this salon.',
    body: 'What began as a Dominican styling tradition grew into a complete, universal beauty experience — caring for every hair type, every texture, every hair history. They have seen it all. They have transformed it all.',
    hairTypes: ['Type 3 curls', 'Natural 4C', 'Chemically damaged', 'Virgin', 'Extensions'],
    tagline: 'Walk in a client. Walk out a Queen.',
  },
  whyWeWin: {
    eyebrow: 'The difference',
    title: 'Why La Reina wins — every time',
    othersHeader: 'Other salons',
    reinaHeader: 'La Reina Beauty Salon',
    rows: [
      { others: 'They rush you without listening', reina: 'We take the time to listen to you' },
      { others: 'One service for everyone', reina: 'Personalized for YOUR hair type' },
      { others: 'They damage, then repair', reina: 'We protect while we transform' },
      { others: 'Generic products', reina: 'Premium professional treatments' },
      { others: 'Cold and transactional', reina: 'Warm, welcoming, like family' },
      { others: 'Only cuts and color', reina: 'Everything in one place' },
      {
        others: 'Results that last a few days',
        reina: 'Results that turn heads all week long',
      },
    ],
  },
  brandPromise: {
    quote:
      'We do not just do hair. We restore confidence, rebuild crowns and remind you who you are.',
    body: 'You should not have to visit one salon for color, another for extensions, another for treatment and one more because none of them got your blowout right. Every client who sits in our chair deserves to leave better than she arrived.',
    stats: [
      { value: '4.7★', label: 'Average rating' },
      { value: '6 days', label: 'Open every week' },
      { value: '100%', label: 'Human hair in store' },
    ],
  },
  schedule: {
    eyebrow: 'Our hours',
    title: 'We are here for you 6 days a week',
    walkIn: 'No appointment needed — just walk in',
    days: [
      { day: 'Monday', hours: '9:00 AM – 6:00 PM' },
      { day: 'Tuesday', hours: '9:00 AM – 6:00 PM' },
      { day: 'Wednesday', hours: '9:00 AM – 6:00 PM' },
      { day: 'Thursday', hours: '9:00 AM – 6:00 PM' },
      { day: 'Friday', hours: '9:00 AM – 7:00 PM', note: 'Extended hours' },
      { day: 'Saturday', hours: '8:00 AM – 6:00 PM', note: 'Early opening' },
      { day: 'Sunday', hours: 'Closed' },
    ],
    friday: {
      lead: 'On ',
      accent: 'Fridays',
      rest: ' we stay open until 7 PM — perfect for women who work during the week.',
    },
    saturday: {
      lead: 'On ',
      accent: 'Saturdays',
      rest: ' we open at 8 AM: start your weekend like the queen you are.',
    },
    privateParties: 'Available for private party bookings with advance notice.',
  },
  contact: {
    eyebrow: 'Walk-ins Welcome · No Appointment Needed',
    title: 'Everything you need is here. Only one thing is missing — you.',
    intro:
      'Call us, message us or simply walk in. Your transformation starts the moment you step through the door.',
    labels: {
      tel: 'Phone',
      cel: 'WhatsApp — bookings',
      dir: 'Address',
      ig: 'Instagram',
      ig2: 'Also follow us on',
      mail: 'Email',
    },
    callNow: 'Call now',
    writeUs: 'Message us',
  },
  footer: {
    quote: 'Walk in a client. Walk out a Queen.',
    notes: [
      'Services for men and women',
      '100% human hair available in store',
      'Dominican and universal styling',
      'Private parties with advance notice',
    ],
    address: 'La Reina Beauty Salon · 2031 Forest Avenue, Staten Island, New York',
    rights: 'All rights reserved.',
  },
  legal: {
    updatedLabel: 'Updated on',
    close: 'Close',
    contactNote:
      'Questions about these policies? Call us at (718) 273-3434, message us on WhatsApp at (347) 853-1793 or email lareinabeautysalon1@gmail.com.',
    privacy: {
      linkLabel: 'Privacy',
      title: 'Privacy Policy',
      intro:
        'At La Reina Beauty Salon LLC (2031 Forest Avenue, Staten Island, NY 10303) we handle your information with the same care we give your hair: we only ask for what we need to take care of you properly.',
      sections: [
        {
          heading: 'What we collect',
          body: [
            'When you book by phone, WhatsApp, Instagram or in person, we keep your name, your contact number and the service you requested.',
            'For chemical services, color, treatments and extensions we also note your hair history (previous treatments, allergies or sensitivities) so we can work safely.',
          ],
        },
        {
          heading: 'How we use it',
          body: [
            'We use your information only to schedule and confirm your visits, remind you of an appointment, follow up on a service and answer your questions.',
            'With your explicit consent we may send you salon news or promotions. You can ask us to stop at any time.',
          ],
        },
        {
          heading: 'Photos and social media',
          body: [
            'We never post photos of your hair without your permission. If you give it, we may share the result on our Instagram or TikTok, and you can ask us to take it down whenever you want.',
          ],
        },
        {
          heading: 'Who we share it with',
          body: [
            'We never sell, rent or trade your personal information.',
            'It is seen only by the team members who need it to serve you, and by the platforms we use to communicate with you (such as WhatsApp or Instagram), which are governed by their own policies.',
          ],
        },
        {
          heading: 'Payments',
          body: [
            'Payments are made in the salon. We do not store full card numbers or banking details — those are processed directly by our payment terminal.',
          ],
        },
        {
          heading: 'Your rights',
          body: [
            'You can ask us at any time what information we hold, request a correction, or ask us to delete it from our records.',
            'Information about minors is only recorded with the consent of a parent or legal guardian.',
          ],
        },
      ],
    },
    terms: {
      linkLabel: 'Terms',
      title: 'Terms of Service',
      intro:
        'These terms explain how service works at La Reina Beauty Salon LLC, so your visit is clear and free of surprises.',
      sections: [
        {
          heading: 'Walk-ins and wait times',
          body: [
            'We serve clients without an appointment. Order of service depends on arrival and on the type of service, so there may be a wait during busy hours.',
            'If you would rather secure your spot, message us on WhatsApp at (347) 853-1793 before you come in.',
          ],
        },
        {
          heading: 'Consultation and results',
          body: [
            'Color, smoothing, chemical treatments and extensions require a consultation first. The result depends on the condition, texture and history of your hair.',
            'If your hair has previous dye, bleach or straightening, we may recommend a process across several sessions to protect its health. We cannot guarantee an identical match to a reference photo.',
          ],
        },
        {
          heading: 'Pricing and payment',
          body: [
            'Prices are confirmed before the service begins and may vary based on length, density, time required and the products needed.',
            '100% human hair and other products sold in the salon are paid for at the time of purchase.',
          ],
        },
        {
          heading: 'Adjustments and refunds',
          body: [
            'Services already performed are non-refundable. If something did not turn out as discussed, let us know within 7 days and we will review and adjust it at no cost where appropriate.',
            'For hygiene reasons, opened or used hair and products cannot be returned.',
          ],
        },
        {
          heading: 'Minors and guests',
          body: [
            'We gladly serve children accompanied by a parent or legal guardian, who must authorize the service and remain in the salon.',
          ],
        },
        {
          heading: 'Personal belongings and aftercare',
          body: [
            'We are not responsible for personal items left behind or left unattended in the salon.',
            'To make your result last, we give you at-home care recommendations. Using the wrong products or heat tools can shorten the life of a service.',
          ],
        },
        {
          heading: 'Private parties and events',
          body: [
            'We can reserve the salon or part of the team for weddings, quinceañeras, graduations and other events with advance notice and a confirmed date.',
          ],
        },
      ],
    },
  },
  gallery: {
    alts: [
      'Geometric braids with precise parts created at La Reina',
      'Layered cut with a Dominican blow dry and glossy finish',
      'High updo with defined curls for a special occasion',
      'Long ponytail with a wavy extension',
      'Sew-in braids with a braided ponytail in a copper tone',
      'Deep wine color with wide waves and mirror shine',
      'Children’s braids with twists and gold accessories',
      'Treated hair with voluminous waves and intense shine',
    ],
  },
}
